import { useState } from 'react';
import { Tree, Button } from 'antd';
import { TagFamilyMemberType, WorkflowDTOType } from '../../../../schemas';
import ItemsByTag from './ItemsByTag';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { ROOT_NODE_NAME } from '../../../Products/UpsertTaggingWorkflowDrawer/JobInfo/TagsFamily/utils';
import { TreeSelectDataNode } from '../ProductsTab/WorkflowItemHorizontal/types';

const Wrapper = styled.div`
  position: relative;
`;

const PillsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px;
  border-bottom: 1px solid #f4f4f4;
  z-index: 2;
`;

const StyledButton = styled(Button)<{ active?: boolean }>`
  ${({ active }) =>
    active
      ? css`
          border-color: #3132eb;
          color: #fff;
          background: #3132eb;
        `
      : css`
          border-color: #171717;
        `}
`;

const TreeContainer = styled(motion.div)<{ visible: boolean }>`
  position: absolute;
  top: 56px;
  left: 0;
  right: 0;
  z-index: 10;
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  display: ${({ visible }) => (visible ? 'block' : 'none')};
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #f4f4f4;

  > * {
    background: inherit;
  }
`;

function tagFamilyToTreeData(tagFamily: Array<TagFamilyMemberType>): Array<TreeSelectDataNode> {
  return tagFamily.map(tag => ({
    title: tag.name,
    value: tag.name,
    key: tag.name,
    checkable: true,
    children: tag.children ? tagFamilyToTreeData(tag.children) : [],
  }));
}

// Custom tree check handler that doesn't use path inclusion
function handleSimpleTreeCheck(checked: any): Array<string> {
  let checkedArr: Array<string> = [];
  if (Array.isArray(checked)) {
    checkedArr = checked as Array<string>;
  } else if (checked && Array.isArray((checked as any).checked)) {
    checkedArr = (checked as any).checked as Array<string>;
  }

  // Return the checked nodes as-is, no path manipulation
  return checkedArr;
}

const NO_TAGS_KEY = 'NO_TAGS';
const NO_TAGS_LABEL = 'No Tags';

const TagsTab = ({ workflow }: { workflow: WorkflowDTOType | null }) => {
  let topLevelTags = workflow?.tagFamily ?? [];

  if (
    topLevelTags.length > 0 &&
    topLevelTags[0].name === ROOT_NODE_NAME &&
    Array.isArray(topLevelTags[0].children)
  ) {
    topLevelTags = topLevelTags[0].children;
  }

  const pills = [
    { name: NO_TAGS_LABEL, key: NO_TAGS_KEY },
    ...topLevelTags.map(tag => ({ name: tag.name, key: tag.name })),
  ];

  const [selectedTopLevel, setSelectedTopLevel] = useState<string | undefined>(
    pills.length > 0 ? pills[0].key : undefined,
  );

  const [selectedTags, setSelectedTags] = useState<Array<string>>([]);
  const [treeOpen, setTreeOpen] = useState(false);

  if (!workflow) return null;

  const selectedTopLevelNode =
    selectedTopLevel && selectedTopLevel !== NO_TAGS_KEY
      ? topLevelTags.find(tag => tag.name === selectedTopLevel)
      : undefined;

  const treeData =
    selectedTopLevelNode && selectedTopLevelNode.children
      ? tagFamilyToTreeData(selectedTopLevelNode.children)
      : [];

  const hasNoChildren =
    selectedTopLevelNode &&
    (!selectedTopLevelNode.children || selectedTopLevelNode.children.length === 0);

  const effectiveSelectedTags = hasNoChildren ? [selectedTopLevelNode.name] : selectedTags;

  let hoverTimeout: NodeJS.Timeout;
  const handlePillsEnter = () => {
    clearTimeout(hoverTimeout);
    setTreeOpen(true);
  };
  const handleTreeLeave = () => {
    hoverTimeout = setTimeout(() => setTreeOpen(false), 120);
  };
  const handleTreeEnter = () => {
    clearTimeout(hoverTimeout);
    setTreeOpen(true);
  };
  const handlePillsLeave = () => {
    hoverTimeout = setTimeout(() => setTreeOpen(false), 120);
  };

  return (
    <Wrapper>
      <PillsWrapper onMouseEnter={handlePillsEnter} onMouseLeave={handlePillsLeave}>
        {pills.map(tag => (
          <StyledButton
            key={tag.key}
            active={selectedTopLevel === tag.key}
            type={selectedTopLevel === tag.key ? 'primary' : 'default'}
            onClick={() => {
              setSelectedTopLevel(tag.key);
              setSelectedTags([]);
            }}
            size="small"
          >
            {tag.name}
          </StyledButton>
        ))}
      </PillsWrapper>
      <AnimatePresence>
        {treeOpen && selectedTopLevel !== NO_TAGS_KEY && (
          <TreeContainer
            visible={treeOpen}
            onMouseEnter={handleTreeEnter}
            onMouseLeave={handleTreeLeave}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.18 }}
          >
            <Tree
              checkStrictly={true}
              treeData={treeData}
              selectable={false}
              defaultExpandAll={true}
              autoExpandParent={true}
              checkable
              checkedKeys={selectedTags}
              onCheck={checked => {
                const result = handleSimpleTreeCheck(checked);
                setSelectedTags(result);
              }}
              style={{
                padding: '16px 0',
              }}
            />
          </TreeContainer>
        )}
      </AnimatePresence>

      {effectiveSelectedTags.length > 0 && (
        <ItemsByTag workflow={workflow} tags={effectiveSelectedTags} />
      )}
    </Wrapper>
  );
};

export default TagsTab;
